import { CustomerServiceOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Layout, Menu } from 'antd'
import { normalizeRoutePath } from 'common/context/commonRoutes.ts'
import { useUserContext } from 'common/context/UserContext.tsx'
import React from 'react'
import { Link } from 'react-router-dom'

import './style.css'
import { AgencyBean } from 'common/api/api.ts'
import FeatureAccessEnum = AgencyBean.FeatureAccessEnum

const { Sider } = Layout

export interface MenuItem {
  label: string
  key: string
  url: string
  icon?: React.ReactNode
  children?: MenuItem[]
  access?: FeatureAccessEnum[]
}

export function menuItem(path: string, child: React.ReactNode, icon?: React.ReactNode): React.ReactElement {
  const href = normalizeRoutePath(path)
  return (
    <Link to={href}>
      {icon && <span className={`${icon} menuLeft__icon`} />}
      <span className="menuLeft__item-title">{child}</span>
    </Link>
  )
}

interface SidebarProps {
  admin: boolean
  menuTabs: MenuItem[]
  selectedKeys: string[]
  openKeys: string[]
  collapsed: boolean
  setCollapsed: (x: boolean) => void
  handleClick: (x: any) => void
  onOpenChange: (x: any) => void
}

export const Sidebar: React.FC<SidebarProps> = ({
  admin,
  menuTabs,
  selectedKeys,
  openKeys,
  collapsed,
  setCollapsed,
  handleClick,
  onOpenChange,
}) => {
  const { logoDataURI, featureAccess } = useUserContext()

  const hasAccess = (access?: FeatureAccessEnum[]) => {
    if (admin) return true
    if (!access || access.length === 0) return true
    if (!featureAccess || featureAccess.length === 0) return true
    return access.some((a) => featureAccess.includes(a))
  }

  const filteredMenuTabs: MenuItem[] = menuTabs
    .map((item) => {
      if (item.children && item.children.length > 0) {
        const visibleChildren = item.children.filter((child) => hasAccess(child.access))

        if (!visibleChildren.length && !hasAccess(item.access)) {
          return null
        }

        return {
          ...item,
          children: visibleChildren,
        }
      }

      return hasAccess(item.access) ? item : null
    })
    .filter((x): x is MenuItem => x !== null)

  const makeItem = (item: MenuItem) => {
    if (item.children?.length) {
      return {
        key: item.key,
        icon: item.icon,
        label: item.label,
        children: item.children
          .map((child) => ({
            key: normalizeRoutePath(child.url),
            icon: child.icon,
            label: menuItem(item.url + child.url, child.label),
          })),
      }
    }
    return {
      key: normalizeRoutePath(item.url.includes('/create') ? item.url.split('/')[0] : item.url),
      icon: item.icon,
      label: menuItem(item.url, item.label),
    }
  }

  const items = filteredMenuTabs.map(makeItem)

  return (
    <Sider
      width={214}
      collapsedWidth={80}
      collapsed={collapsed}
      trigger={null}
      className="menuLeft"
      style={{ height: '100vh', position: 'sticky', top: 0 }}
    >
      <div className="menuLeft__inner">
        <div className="menuLeft__logo">
          <div
            style={{
              height: 40,
              maxWidth: collapsed ? 34 : 169,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'max-width .3s',
            }}
          >
            {!collapsed && logoDataURI && (
              <img
                src={logoDataURI}
                alt="Logo"
                style={{
                  width: 160,
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            )}
          </div>
        </div>

        <div className="menuLeft__scroll">
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={handleClick}
            items={items}
            className="sidebar__menu"
            _internalDisableMenuItemTitleTooltip
          />
        </div>

        <div className="menuLeft__footer">
          {!admin && (
            <Button
              type="text"
              href="https://asterio.ai/#contact"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Support"
              className="menuLeft__support"
              icon={<CustomerServiceOutlined />}
            >
              {!collapsed && 'Support'}
            </Button>
          )}
          <Button
            type="text"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setCollapsed(!collapsed)}
            className="menuLeft__trigger"
            icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          />
        </div>
      </div>
    </Sider>
  )
}
